import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getLocalizedUrl,
  parseLocalizedUrl,
  isOldRoute
} from '../../config/route-translations';

/**
 * Component that redirects old routes to new language-prefixed routes
 * This runs immediately when the route is matched, before rendering
 */
const LanguageRedirect = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;
  const { language: pathLanguage, route: pathRoute } = parseLocalizedUrl(
    pathname
  );

  useEffect(() => {
    // Skip redirects for admin, auth, cargo, and special routes
    const skipRedirectPaths = [
      '/admin',
      '/login',
      '/register',
      '/reset_password',
      '/recover_password',
      '/cargo',
      '/form/',
      '/home'
    ];
    const shouldSkipRedirect = skipRedirectPaths.some(path =>
      pathname.startsWith(path)
    );

    // Handle old blog listing routes first (before checking pathLanguage)
    // These need to redirect even though parseLocalizedUrl detects a language
    if (pathname === '/blog/es' || pathname === '/blog/en') {
      const urlParams = new URLSearchParams(location.search);
      const languageParam = urlParams.get('language');
      const currentLanguage =
        languageParam || localStorage.getItem('language') || 'es';
      const localizedUrl =
        currentLanguage === 'es'
          ? '/es/blog-viajes-ferry-chile'
          : '/en/chile-ferry-travel-blog';

      if (pathname !== localizedUrl) {
        const newSearch = location.search.replace(/[?&]language=[^&]*/, '');
        navigate(localizedUrl + newSearch, { replace: true });
      }
      return;
    }

    // If already on a language-prefixed URL (like /es/route or /en/route), no redirect needed
    // Also skip root path (/) - handled in App.js
    // Note: /blog/es and /blog/en are handled above, so we skip them here
    if (
      (pathLanguage && pathname.startsWith(`/${pathLanguage}/`)) ||
      shouldSkipRedirect ||
      pathname === '/' ||
      pathname === '/es' ||
      pathname === '/en'
    ) {
      return;
    }

    // Check for language in query params first
    const urlParams = new URLSearchParams(location.search);
    const languageParam = urlParams.get('language');
    let currentLanguage =
      languageParam || localStorage.getItem('language') || 'es';

    // Update localStorage if language param is present
    if (languageParam) {
      localStorage.setItem('language', languageParam);
      currentLanguage = languageParam;
    }

    const baseRoute = pathRoute || pathname.replace(/^\//, '');

    // Check if this is an old route that should be redirected
    if (isOldRoute(baseRoute)) {
      let localizedUrl;

      // Handle special cases
      if (
        baseRoute === 'public/blogs' ||
        pathname.startsWith('/public/blogs')
      ) {
        // Redirect to blog entry point
        localizedUrl =
          currentLanguage === 'es'
            ? '/es/blog-viajes-ferry-chile'
            : '/en/chile-ferry-travel-blog';
      } else if (
        pathname.startsWith('/blog/es/') ||
        pathname.startsWith('/blog/en/')
      ) {
        // Blog posts - maintain /blog/es/slug and /blog/en/slug structure (don't redirect)
        // This is handled by LanguageDropdown, not here
        return;
      } else if (
        pathname === '/es/blog-viajes-ferry-chile' ||
        pathname === '/en/chile-ferry-travel-blog'
      ) {
        // Blog entry point - already correct, no redirect needed
        return;
      } else if (baseRoute === 'public/faqs') {
        localizedUrl =
          currentLanguage === 'es'
            ? '/es/preguntas-frecuentes-ferry-pasajeros'
            : '/en/passenger-ferry-faq';
      } else if (baseRoute === 'what-to-expect') {
        localizedUrl =
          currentLanguage === 'es'
            ? '/es/que-esperar-del-viaje'
            : '/en/what-to-expect-from-your-trip';
      } else {
        // Use the standard translation
        localizedUrl = getLocalizedUrl(baseRoute, currentLanguage);
      }

      // Always redirect old routes to new language-prefixed URLs
      if (pathname !== localizedUrl) {
        const newSearch = location.search.replace(/[?&]language=[^&]*/, '');
        navigate(localizedUrl + newSearch, { replace: true });
      }
    }
  }, [pathname, pathLanguage, pathRoute, navigate, location.search]);

  return children;
};

export default LanguageRedirect;
